import { lazy, startTransition, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, keyframes } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SendIcon from "@mui/icons-material/SendRounded";

import { useTranslation } from "react-i18next";
import { assistantApi } from "../api/endpoints";
import { useLocale } from "../hooks/useLocale";
import { motion } from "../theme/theme";
import { ASSISTANT_EXPANSION_MS, assistantExpansionFrames } from "../utils/assistantExpansion";
import { helpTextDelay, nextHelpTextFrame } from "../utils/helpTypewriter";
import { assistantSuggestions } from "../utils/assistantSuggestions";
import { LoadingState } from "./LoadingState";
import { AtlasMark } from "./atlasMark/AtlasMark";

const LAST_CONVERSATION_KEY = "atlas.ultimaConversacion";
const RichMessage = lazy(() => import("./RichMessage").then(module => ({ default: module.RichMessage })));

function HelpPrompt() {
  const { t } = useTranslation();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const phrases = [t("assistantWidget.needHelp"), t("assistantWidget.productQuestion")];
  const [frame, setFrame] = useState({ index: 0, count: 0, erasing: false });
  const phrase = phrases[reducedMotion ? 0 : frame.index];
  useEffect(() => {
    if (reducedMotion) return undefined;
    const timer = window.setTimeout(
      () => setFrame(current => nextHelpTextFrame(current, phrase.length)),
      helpTextDelay(frame, phrase.length),
    );
    return () => window.clearTimeout(timer);
  }, [frame, phrase, reducedMotion]);
  return (
    <Typography component="span" variant="body2" aria-label={phrase} sx={{ display: "inline-flex", textAlign: "left", minWidth: "1ch" }}>
      <Box component="span" aria-hidden sx={{ whiteSpace: "nowrap" }}>
        {reducedMotion ? phrase : phrase.slice(0, frame.count)}
        {!reducedMotion && <Box component="span" sx={{ borderRight: "1px solid", borderColor: "primary.main", ml: "2px" }} />}
      </Box>
    </Typography>
  );
}

const aparecer = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

/**
 * La mascota del asistente: la A de Atlas en 3D (ver `AtlasMark`). Mientras
 * el asistente prepara una respuesta, «piensa» girando la cabeza.
 */
export function MascotVisual({ size = 68, animated = true, mode = "reposo", greet = false }) {
  return <AtlasMark size={size} mode={animated ? mode : "reposo"} greet={animated && greet} />;
}

export function AssistantMascot() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const widget = useRef(null);
  const messageArea = useRef(null);
  const panel = useRef(null);
  const expansion = useRef(null);
  const expansionPending = useRef(false);
  const [expanding, setExpanding] = useState(false);
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => () => {
    expansionPending.current = false;
    expansion.current?.cancel();
  }, []);

  useLayoutEffect(() => {
    const element = widget.current;
    const layout = element?.parentElement;
    if (!layout) return undefined;
    let footers = [];
    const measure = () => {
      const bottom = footers.reduce((offset, footer) => {
        const rect = footer.getBoundingClientRect();
        if (!rect.width || !rect.height || rect.top >= window.innerHeight || rect.bottom <= 0) return offset;
        return Math.max(offset, window.innerHeight - rect.top + 16);
      }, 24);
      element.style.setProperty("--assistant-mascot-bottom", `${Math.ceil(bottom)}px`);
    };
    const resize = new ResizeObserver(measure);
    const refresh = () => {
      const next = [...layout.querySelectorAll(".portal-flow-footer")];
      if (next.length !== footers.length || next.some((footer, index) => footer !== footers[index])) {
        resize.disconnect();
        footers = next;
        footers.forEach((footer) => resize.observe(footer));
      }
      measure();
    };
    // Los pasos pueden cargar la barra después de recibir la solicitud.
    const mutations = new MutationObserver(refresh);
    mutations.observe(layout, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    refresh();
    return () => {
      resize.disconnect();
      mutations.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    const loadConversation = async () => {
      const { conversations } = await assistantApi.conversations();
      const preferred = window.localStorage.getItem(LAST_CONVERSATION_KEY);
      let conversation =
        conversations.find((item) => item.id === preferred) ?? conversations[0];
      if (!conversation) {
        ({ conversation } = await assistantApi.createConversation());
      }
      const data = await assistantApi.conversation(conversation.id);
      if (cancelled) return;
      setConversationId(conversation.id);
      setExpiresAt(data.conversation?.expiresAt ?? conversation.expiresAt);
      window.localStorage.setItem(LAST_CONVERSATION_KEY, conversation.id);
      setMessages(data.messages ?? []);
    };

    loadConversation()
      .catch((loadError) => {
        if (!cancelled) setError(loadError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const expire = () => {
      if (Date.now() < Date.parse(expiresAt)) return;
      expansionPending.current = false;
      expansion.current?.cancel();
      setExpanding(false);
      setOpen(false);
      setMessages([]);
      setText("");
      setConversationId(null);
      setExpiresAt(null);
      window.localStorage.removeItem(LAST_CONVERSATION_KEY);
    };
    const timer = window.setTimeout(expire, Math.max(0, Date.parse(expiresAt) - Date.now()));
    window.addEventListener("focus", expire);
    document.addEventListener("visibilitychange", expire);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", expire);
      document.removeEventListener("visibilitychange", expire);
    };
  }, [expiresAt]);

  useEffect(() => {
    const area = messageArea.current;
    if (area) area.scrollTop = area.scrollHeight;
  }, [messages, sending, open]);

  const send = useCallback(async (content = text) => {
    const clean = content.trim();
    if (!clean || sending || loading || expanding || !conversationId) return;
    const temporaryId = `mini-${Date.now()}`;
    setText("");
    setSending(true);
    setError(null);
    setMessages((current) => [
      ...current,
      { id: temporaryId, role: "user", content: clean },
    ]);
    try {
      const data = await assistantApi.send(conversationId, clean, locale);
      if (expiresAt && Date.parse(expiresAt) <= Date.now()) return;
      setMessages((current) => [
        ...current.filter((item) => item.id !== temporaryId),
        data.userMessage,
        data.assistantMessage,
      ]);
    } catch (sendError) {
      if (expiresAt && Date.parse(expiresAt) <= Date.now()) return;
      setMessages((current) =>
        current.filter((item) => item.id !== temporaryId),
      );
      setText(clean);
      setError(sendError);
    } finally {
      setSending(false);
    }
  }, [text, sending, loading, expanding, conversationId, locale, expiresAt]);

  const expand = async () => {
    if (expansionPending.current || sending || loading) return;
    expansionPending.current = true;
    setExpanding(true);
    const element = panel.current;
    const originalStyle = element?.getAttribute("style");
    try {
      const destination = document.querySelector("[data-portal-content]");
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (element?.animate && destination && !reducedMotion) {
        const from = element.getBoundingClientRect();
        const to = destination.getBoundingClientRect();
        const frames = assistantExpansionFrames(from, { ...to.toJSON(), height: window.innerHeight });
        Object.assign(element.style, frames[0], { position: "fixed", margin: "0", animation: "none", transform: "none" });
        expansion.current = element.animate(frames, {
          duration: ASSISTANT_EXPANSION_MS,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
          fill: "forwards",
        });
      }
      // Carga el destino durante la ampliación, manteniendo el panel hasta
      // que React pueda mostrar Assist sin una pantalla vacía intermedia.
      await Promise.all([import("../pages/AssistantPage"), expansion.current?.finished]);
      if (!expansionPending.current) return;
      startTransition(() => navigate("/asistente", {
        state: { assistantExpanded: true, assistantHandoff: { conversationId, messages, text, expiresAt } },
      }));
    } catch (expandError) {
      if (!expansionPending.current) return;
      expansion.current?.cancel();
      if (element) {
        if (originalStyle === null) element.removeAttribute("style");
        else element.setAttribute("style", originalStyle);
      }
      expansionPending.current = false;
      setExpanding(false);
      setError(expandError);
    }
  };

  const toggle = () => {
    if (expansionPending.current) return;
    if (!open) {
      setLoading(true);
      setError(null);
    }
    setOpen((current) => !current);
  };

  return (
    <Box
      ref={widget}
      sx={{
        position: { xs: "absolute", md: "fixed" },
        right: { xs: 14, md: 24 },
        bottom: { xs: "calc(72px + env(safe-area-inset-bottom))", md: "var(--assistant-mascot-bottom, 24px)" },
        zIndex: (theme) => theme.zIndex.appBar + 2,
        pointerEvents: "none",
        display: { xs: "none", md: "flex" },
        alignItems: "flex-end",
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      {!open && (
        <Paper
          component="button"
          type="button"
          onClick={toggle}
          elevation={3}
          sx={{
            pointerEvents: "auto", position: "relative", mb: 2, px: 2, py: 1.25,
            border: "1px solid", borderColor: "divider", borderRadius: "12px",
            color: "text.primary", font: "inherit", cursor: "pointer",
            "&:hover": { bgcolor: "action.hover" },
            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 3 },
            "&::after": {
              content: '""', position: "absolute", right: -5, bottom: 12,
              width: 8, height: 8, bgcolor: "background.paper", transform: "rotate(45deg)",
              borderTop: "1px solid", borderRight: "1px solid", borderColor: "divider",
            },
          }}
        >
          <HelpPrompt />
        </Paper>
      )}
      {open && (
        <Paper
          ref={panel}
          elevation={12}
          role="dialog"
          aria-label={t("assistantWidget.title")}
          sx={{
            pointerEvents: "auto",
            width: { xs: "calc(100vw - 104px)", sm: 440 },
            height: { xs: "min(520px, calc(100vh - 150px))", sm: "min(500px, calc(100dvh - var(--assistant-mascot-bottom, 24px) - 80px))" },
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
            animation: `${aparecer} 220ms ${motion.entrada}`,
            transformOrigin: "bottom right",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ alignItems: "center", p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                {t("assistantWidget.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("assistantWidget.status")}
              </Typography>
            </Box>
            <Tooltip title={t("assistantWidget.expand")}>
              <span>
                <IconButton aria-label={t("assistantWidget.expand")} onClick={expand} disabled={sending || loading || expanding}>
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton aria-label={t("common.close")} onClick={() => setOpen(false)} disabled={expanding}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box ref={messageArea} sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
            {loading ? (
              <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
                <LoadingState layout="messages" height={160} />
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {messages.length === 0 && (
                  <>
                    <Box sx={{ alignSelf: "flex-start", bgcolor: "action.hover", borderRadius: "16px 16px 16px 5px", px: 1.5, py: 1.1, maxWidth: "88%" }}>
                      <Typography variant="body2">{t("assistantWidget.greeting")}</Typography>
                    </Box>
                    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                      {assistantSuggestions(t).map(suggestion => (
                        <Chip
                          key={suggestion}
                          label={suggestion}
                          variant="outlined"
                          disabled={sending || expanding || !conversationId}
                          onClick={() => void send(suggestion)}
                          sx={{ height: "auto", maxWidth: "100%", py: 0.75, "& .MuiChip-label": { whiteSpace: "normal" } }}
                        />
                      ))}
                    </Stack>
                  </>
                )}
                {messages.slice(-10).map((message) => {
                  const userMessage = message.role === "user";
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        alignSelf: userMessage ? "flex-end" : "flex-start",
                        bgcolor: userMessage ? "primary.main" : "action.hover",
                        color: userMessage ? "primary.contrastText" : "text.primary",
                        borderRadius: userMessage ? "16px 16px 5px 16px" : "16px 16px 16px 5px",
                        px: 1.5,
                        py: 1.1,
                        maxWidth: "88%",
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        "& a": { overflowWrap: "anywhere" },
                        "& .MuiTableContainer-root": { mx: 0, maxWidth: "100%" },
                      }}
                    >
                      {userMessage ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {message.content}
                        </Typography>
                      ) : (
                        <Suspense fallback={<LoadingState layout="messages" height={96} />}>
                          <RichMessage>{message.content}</RichMessage>
                        </Suspense>
                      )}
                    </Box>
                  );
                })}
                {sending && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", color: "text.secondary" }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption">{t("assistantWidget.thinking")}</Typography>
                  </Stack>
                )}
                {error && (
                  <Alert severity="error" sx={{ py: 0 }}>
                    {t(error.code ?? error.message ?? "errors.assistantFailed")}
                  </Alert>
                )}
              </Stack>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "flex-end", p: 1.25, borderTop: "1px solid", borderColor: "divider" }}
          >
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder={t("assistantWidget.placeholder")}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              disabled={loading || expanding}
              slotProps={{ htmlInput: { maxLength: 4000 } }}
            />
            <IconButton
              color="primary"
              aria-label={t("assistantWidget.send")}
              onClick={() => void send()}
              disabled={loading || sending || expanding || !text.trim() || !conversationId}
            >
              <SendIcon />
            </IconButton>
          </Stack>
        </Paper>
      )}

      <Tooltip title={t("assistantWidget.open")} placement="left">
        <IconButton
          aria-label={t("assistantWidget.open")}
          aria-expanded={open}
          disabled={expanding}
          onClick={toggle}
          sx={{ p: 0, pointerEvents: "auto", overflow: "visible" }}
        >
          <MascotVisual size={84} mode={sending ? "pensando" : "reposo"} greet />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
